import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { History, Mic, ClipboardPaste, Trash2, X, ClipboardCopy, Sparkles } from 'lucide-react';
import { useInputHistoryStore, type InputHistoryEntry } from '@/stores/inputHistoryStore';
import { useBackend } from '@/services/backend';
import { logger } from '@/lib/logger';
import { writeTextToPty } from '@/lib/base64';

const TAG = 'InputHistory';

interface InputHistoryButtonProps {
  activeSessionId: number | null;
  projectPath: string;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString();
}

function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen - 1) + '\u2026';
}

export default function InputHistoryButton({ activeSessionId, projectPath }: InputHistoryButtonProps) {
  const backend = useBackend();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const entries = useInputHistoryStore((s) => s.getEntries(projectPath));
  const removeEntry = useInputHistoryStore((s) => s.removeEntry);
  const clearProject = useInputHistoryStore((s) => s.clearProject);

  // Per-entry "show the raw transcript instead of the AI-enhanced version"
  // toggle, keyed by entry id. Only meaningful for enhanced entries that carry
  // a distinct `rawText`. Defaults to showing the enhanced text (absent = false).
  const [showRaw, setShowRaw] = useState<Record<string, boolean>>({});
  const shownText = useCallback(
    (entry: InputHistoryEntry) =>
      showRaw[entry.id] && entry.rawText ? entry.rawText : entry.text,
    [showRaw],
  );

  const togglePanel = useCallback(() => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 4,
        left: Math.max(8, rect.right - 320),
      });
    }
    setOpen((v) => !v);
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const insertEntry = useCallback(async (text: string) => {
    if (activeSessionId === null) return;
    try {
      await writeTextToPty(text, activeSessionId, (id, data) => backend.terminal.write(id, data));
    } catch (e) {
      logger.warn(TAG, 'write failed', e);
    }
    setOpen(false);
  }, [activeSessionId, backend]);

  const hasEntries = entries.length > 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={togglePanel}
        className={`flex items-center gap-1 h-6 px-1.5 rounded transition-colors ${
          hasEntries
            ? 'text-base-content/60 hover:text-base-content hover:bg-base-content/10 cursor-pointer'
            : 'text-stone-600 cursor-default'
        }`}
        title={hasEntries ? `Input history (${entries.length})` : 'No input history'}
        disabled={!hasEntries}
      >
        <History className="w-3.5 h-3.5" />
        {hasEntries && (
          <span className="text-[10px] tabular-nums text-base-content/50">{entries.length}</span>
        )}
      </button>

      {open && hasEntries && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[9999] bg-base-100 border border-base-300 rounded-lg shadow-xl w-80 max-h-80 flex flex-col"
          style={{ top: pos.top, left: pos.left }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-base-300">
            <span className="text-xs font-medium text-stone-300">Input History</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { clearProject(projectPath); setOpen(false); }}
                className="text-base-content/50 hover:text-red-500 p-0.5 rounded transition-colors"
                title="Clear all"
              >
                <Trash2 className="w-3 h-3" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="text-base-content/50 hover:text-base-content p-0.5 rounded transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Entries */}
          <div className="overflow-y-auto flex-1 py-1">
            {entries.map((entry) => {
              const hasRaw = !!entry.rawText;
              const raw = hasRaw && showRaw[entry.id];
              const text = shownText(entry);
              return (
              <button
                key={entry.id}
                onClick={() => insertEntry(text)}
                className="w-full text-left px-3 py-2 hover:bg-base-200 transition-colors group flex items-start gap-2"
                title={`Click to insert:\n${text}`}
                disabled={activeSessionId === null}
              >
                <span className="flex-shrink-0 mt-0.5">
                  {entry.source === 'prompt' ? (
                    <Sparkles className="w-3 h-3 text-violet-400/80" />
                  ) : entry.source === 'transcription' ? (
                    <Mic className="w-3 h-3 text-amber-500/70" />
                  ) : (
                    <ClipboardPaste className="w-3 h-3 text-blue-400/70" />
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  {/* Enhanced ⇄ Raw toggle — only for entries that carry a
                      distinct raw origin. Segmented control; clicking a segment
                      flips which version is shown / copied / inserted. */}
                  {hasRaw && (
                    <div
                      className="inline-flex items-center gap-px mb-1 rounded bg-base-300/60 p-px text-[9px] font-medium"
                      role="group"
                    >
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setShowRaw((m) => ({ ...m, [entry.id]: false })); }}
                        className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded cursor-pointer ${
                          !raw ? 'bg-violet-500/25 text-violet-200' : 'text-base-content/50 hover:text-base-content/80'
                        }`}
                      >
                        <Sparkles className="w-2.5 h-2.5" /> Enhanced
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setShowRaw((m) => ({ ...m, [entry.id]: true })); }}
                        className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded cursor-pointer ${
                          raw ? 'bg-amber-500/25 text-amber-200' : 'text-base-content/50 hover:text-base-content/80'
                        }`}
                      >
                        <Mic className="w-2.5 h-2.5" /> Raw
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-stone-300 whitespace-pre-wrap break-words leading-relaxed">
                    {truncate(text, 200)}
                  </p>
                  <p className="text-[10px] text-base-content/50 mt-0.5">{formatTime(entry.timestamp)}</p>
                </div>
                <div className="flex-shrink-0 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-all">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(text).catch((e) => logger.warn(TAG, 'clipboard copy failed', e));
                    }}
                    className="text-stone-600 hover:text-stone-200 p-0.5"
                    title={hasRaw ? `Copy ${raw ? 'raw' : 'enhanced'} version` : 'Copy to clipboard'}
                  >
                    <ClipboardCopy className="w-3 h-3" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeEntry(entry.id); }}
                    className="text-stone-600 hover:text-red-400 p-0.5"
                    title="Remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
